import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Person as PersonIcon,
  School as AcademyIcon,
  Assessment as AssessmentIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate } from 'react-router-dom';
import AcademySearch from '@components/AcademySearch';
import { SPORTS } from '@utils/sports';
import { toast } from 'react-toastify';

const CoachProfile: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalStudents: 0,
    juniorCoaches: 0,
    trainingSessions: 0,
  });
  const [formData, setFormData] = useState({
    first_name: (user as any)?.firstName || user?.firstName || '',
    last_name: (user as any)?.lastName || user?.lastName || '',
    email: user?.email || '',
    academy_id: (user as any)?.academyId || null,
    academy_name: user?.academyName || '',
    primary_sport: (user as any)?.primarySport || '',
    secondary_sport: (user as any)?.secondarySport || '',
    coach_level: (user as any)?.coachLevel || 'independent',
    sparring_opt_in: (user as any)?.sparringOptIn === true,
  });

  // Load coach data and statistics
  useEffect(() => {
    const loadCoachData = async () => {
      if (user && user.role === 'coach' && user.id) {
        setLoading(true);
        try {
          // Always fetch fresh data from API
          const response = await fetch(`/api/coaches/me?userId=${user.id}`);
          const data = await response.json();
          if (data.success && data.data) {
            const coachData = data.data;
            // Update user object with all coach data
            const updatedUser = {
              ...user,
              coachId: coachData.id,
              coachIdCode: coachData.coachIdCode,
              firstName: coachData.user.firstName,
              lastName: coachData.user.lastName,
              primarySport: coachData.primarySport,
              secondarySport: coachData.secondarySport,
              coachLevel: coachData.coachLevel,
              sparringOptIn: coachData.sparringOptIn === true,
              academyName: coachData.academyName,
            };
            setUser(updatedUser);
            
            // Update form data
            setFormData({
              first_name: coachData.user.firstName || '',
              last_name: coachData.user.lastName || '',
              email: coachData.user.email || '',
              academy_id: (user as any)?.academyId || null,
              academy_name: coachData.academyName || '',
              primary_sport: coachData.primarySport || '',
              secondary_sport: coachData.secondarySport || '',
              coach_level: coachData.coachLevel || 'independent',
              sparring_opt_in: coachData.sparringOptIn === true,
            });
            
            // Load statistics
            const coachId = coachData.id;
            await loadStatistics(coachId);
          }
        } catch (err) {
          console.error('Error loading coach data:', err);
        } finally {
          setLoading(false);
        }
      } else if (user) {
        setFormData({
          first_name: (user as any)?.firstName || user?.firstName || '',
          last_name: (user as any)?.lastName || user?.lastName || '',
          email: user?.email || '',
          academy_id: (user as any)?.academyId || null,
          academy_name: user.academyName || '',
          primary_sport: (user as any)?.primarySport || '',
          secondary_sport: (user as any)?.secondarySport || '',
          coach_level: (user as any)?.coachLevel || 'independent',
          sparring_opt_in: (user as any)?.sparringOptIn === true,
        });
      }
    };
    
    loadCoachData();
  }, [user, setUser]);
  
  const loadStatistics = async (coachId: string) => {
    try {
      // Get assigned players count
      const playersResponse = await fetch(`/api/coaches/${coachId}/assigned-players`);
      const playersData = await playersResponse.json();
      const totalStudents = playersData.success ? (playersData.data?.length || 0) : 0;
      
      // Get junior coaches count (coaches where this coach is the head coach)
      const hierarchyResponse = await fetch(`/api/coaches/hierarchy/${coachId}`);
      const hierarchyData = await hierarchyResponse.json();
      const juniorCoaches = hierarchyData.success && hierarchyData.data?.juniorCoaches ? hierarchyData.data.juniorCoaches.length : 0;
      
      // Training sessions - count from fitness entries and evaluations
      // This is a simplified count - you may want to enhance this
      let trainingSessions = 0;
      if (playersData.success && playersData.data) {
        // Count unique sessions (simplified - count entries per player)
        for (const player of playersData.data) {
          try {
            const fitnessResponse = await fetch(`/api/performance/fitness/player/${player.id}`);
            const fitnessData = await fitnessResponse.json();
            if (fitnessData.success && fitnessData.data) {
              trainingSessions += fitnessData.data.length;
            }
          } catch (err) {
            // Ignore errors for individual players
          }
        }
      }
      
      setStats({
        totalStudents,
        juniorCoaches,
        trainingSessions,
      });
    } catch (err) {
      console.error('Error loading statistics:', err);
      // Set to 0 if error
      setStats({ totalStudents: 0, juniorCoaches: 0, trainingSessions: 0 });
    }
  };

  const handleBack = () => {
    navigate('/coach');
  };

  const handleEditProfile = () => {
    setEditing(true);
  };

  const handleSaveProfile = async () => {
    try {
      setError(null);
      
      // Get coach ID from user object
      const coachId = (user as any)?.coachId || (user as any)?.id;
      if (!coachId) {
        setError('Coach ID not found');
        return;
      }
      
      // Update coach via API
      const response = await fetch(`/api/coaches/${coachId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.first_name,
          lastName: formData.last_name,
          email: formData.email, // Include email in update request
          academyId: formData.academy_id,
          academyName: formData.academy_name,
          primarySport: formData.primary_sport,
          secondarySport: formData.secondary_sport,
          coachLevel: formData.coach_level,
          sparringOptIn: formData.sparring_opt_in,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Reload coach data from API to get fresh data
        const refreshResponse = await fetch(`/api/coaches/me?userId=${user?.id}`);
        const refreshData = await refreshResponse.json();
        if (refreshData.success && refreshData.data) {
          const coachData = refreshData.data;
          const updatedUser = {
            ...user,
            coachId: coachData.id,
            coachIdCode: coachData.coachIdCode,
            firstName: coachData.user.firstName,
            lastName: coachData.user.lastName,
            email: coachData.user.email,
            primarySport: coachData.primarySport,
            secondarySport: coachData.secondarySport,
            coachLevel: coachData.coachLevel,
            sparringOptIn: coachData.sparringOptIn === true,
            academyName: coachData.academyName,
          };
          setUser(updatedUser);
          
          // Update form data
          setFormData({
            first_name: coachData.user.firstName || '',
            last_name: coachData.user.lastName || '',
            email: coachData.user.email || '',
            academy_id: (user as any)?.academyId || null,
            academy_name: coachData.academyName || '',
            primary_sport: coachData.primarySport || '',
            secondary_sport: coachData.secondarySport || '',
            coach_level: coachData.coachLevel || 'independent',
            sparring_opt_in: coachData.sparringOptIn === true,
          });
          
          // Reload statistics
          if (coachData.id) {
            await loadStatistics(coachData.id);
          }
        }
        
        setEditing(false);
        toast.success('Profile updated successfully!');
        
        // Dispatch event to notify other pages that coach profile was updated
        window.dispatchEvent(new CustomEvent('coachProfileUpdated', { 
          detail: { coachId, firstName: formData.first_name, lastName: formData.last_name } 
        }));
      } else {
        setError(data.message || 'Failed to update profile');
      }
    } catch (err: any) {
      console.error('Error saving profile:', err);
      setError(err.message || 'Failed to save profile');
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    // Reset form data from user state
    if (user) {
      setFormData({
        first_name: user.firstName || '',
        last_name: user.lastName || '',
        email: user.email || '',
        academy_id: (user as any)?.academyId || null,
        academy_name: user.academyName || '',
        primary_sport: (user as any)?.primarySport || '',
        secondary_sport: (user as any)?.secondarySport || '',
        coach_level: (user as any)?.coachLevel || 'independent',
        sparring_opt_in: (user as any)?.sparringOptIn === true,
      });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" component="h1">
            Coach Profile
          </Typography>
        </Box>
        {editing ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleCancelEdit}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveProfile}
            >
              Save Changes
            </Button>
          </Box>
        ) : (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEditProfile}
          >
            Edit Profile
          </Button>
        )}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon color="primary" sx={{ mr: 2, fontSize: 30 }} />
                <Typography variant="h6">
                  Personal Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {editing ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Coach ID"
                      value={(user as any)?.coachIdCode || 'N/A'}
                      disabled
                      helperText="Coach ID cannot be changed"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Coach Level</InputLabel>
                      <Select
                        value={formData.coach_level}
                        label="Coach Level"
                        onChange={(e) => setFormData({ ...formData, coach_level: e.target.value })}
                      >
                        <MenuItem value="independent">Independent</MenuItem>
                        <MenuItem value="head_coach">Head Coach</MenuItem>
                        <MenuItem value="junior_coach">Junior Coach</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Open to Sparring</InputLabel>
                      <Select
                        value={formData.sparring_opt_in ? 'yes' : 'no'}
                        label="Open to Sparring"
                        onChange={(e) => setFormData({ ...formData, sparring_opt_in: e.target.value === 'yes' })}
                      >
                        <MenuItem value="no">No</MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Primary Sport</InputLabel>
                      <Select
                        value={formData.primary_sport}
                        label="Primary Sport"
                        onChange={(e) => setFormData({ ...formData, primary_sport: e.target.value })}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {SPORTS.map((sport) => (
                          <MenuItem key={sport} value={sport}>
                            {sport}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Secondary Sport</InputLabel>
                      <Select
                        value={formData.secondary_sport}
                        label="Secondary Sport"
                        onChange={(e) => setFormData({ ...formData, secondary_sport: e.target.value })}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {SPORTS.map((sport) => (
                          <MenuItem key={sport} value={sport}>
                            {sport}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Full Name"
                      secondary={`${(user as any)?.firstName || user?.firstName || 'N/A'} ${(user as any)?.lastName || user?.lastName || 'N/A'}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Coach ID"
                      secondary={(user as any)?.coachIdCode || 'N/A'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Email"
                      secondary={user?.email || 'N/A'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Role"
                      secondary={
                        <Chip 
                          label={user?.role || 'N/A'} 
                          color="primary" 
                          size="small" 
                        />
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Coach Level"
                      secondary={
                        <Chip 
                          label={(user as any)?.coachLevel || 'N/A'} 
                          color={(user as any)?.coachLevel === 'head_coach' ? 'success' : 'default'} 
                          size="small" 
                        />
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Primary Sport"
                      secondary={(user as any)?.primarySport || 'Not specified'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Secondary Sport"
                      secondary={(user as any)?.secondarySport || 'Not specified'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Sparring Preference"
                      secondary={
                        <Chip
                          label={(user as any)?.sparringOptIn ? 'Open to Sparring' : 'Not Open to Sparring'}
                          color={(user as any)?.sparringOptIn ? 'warning' : 'default'}
                          size="small"
                        />
                      }
                    />
                  </ListItem>
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Academy Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AcademyIcon color="primary" sx={{ mr: 2, fontSize: 30 }} />
                <Typography variant="h6">
                  Academy Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {editing ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <AcademySearch
                      value={formData.academy_id}
                      onChange={(academyId, academyName) => {
                        setFormData({
                          ...formData,
                          academy_id: academyId,
                          academy_name: academyName || '',
                        });
                      }}
                      label="Select Academy"
                    />
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Academy Name"
                      secondary={formData.academy_name || 'Not assigned'}
                    />
                  </ListItem>
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssessmentIcon color="primary" sx={{ mr: 2, fontSize: 30 }} />
                <Typography variant="h6">
                  Coaching Statistics
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {stats.totalStudents}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Students
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {stats.juniorCoaches}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Junior Coaches
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {stats.trainingSessions}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Training Sessions
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CoachProfile;
